/**
 * CitationCard - Enhanced Citation Display for Sage
 *
 * Displays rich citation information with icons, dates, and clickable links
 * to navigate to source data within the app.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  DollarSign,
  Calendar,
  Target,
  Users,
  Scale,
  TrendingDown,
  Landmark,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/posthog';
import { sageEvents } from '@/lib/analytics/events/sage';
import type { DataSource } from '@/hooks/useSageChat';

interface CitationCardProps {
  sources: DataSource[];
  className?: string;
}

// Icon mapping for source types
const SOURCE_ICONS: Record<DataSource['type'], React.ElementType> = {
  correspondence: FileText,
  asset: DollarSign,
  debt: TrendingDown,
  financial: DollarSign,
  deadline: Calendar,
  goal: Target,
  children: Users,
  strategy: Scale,
  state_guidelines: Landmark,
};

// Display labels for source types
const SOURCE_LABELS: Record<DataSource['type'], string> = {
  correspondence: 'Document',
  asset: 'Asset',
  debt: 'Debt',
  financial: 'Financial',
  deadline: 'Deadline',
  goal: 'Goal',
  children: 'Child',
  strategy: 'Strategy',
  state_guidelines: 'State Law',
};

// Color classes for source type badges
const SOURCE_COLORS: Record<DataSource['type'], string> = {
  correspondence: 'bg-blue-100 text-blue-700 border-blue-200',
  asset: 'bg-green-100 text-green-700 border-green-200',
  debt: 'bg-red-100 text-red-700 border-red-200',
  financial: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  deadline: 'bg-amber-100 text-amber-700 border-amber-200',
  goal: 'bg-purple-100 text-purple-700 border-purple-200',
  children: 'bg-pink-100 text-pink-700 border-pink-200',
  strategy: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  state_guidelines: 'bg-slate-100 text-slate-700 border-slate-200',
};

function formatDate(dateStr?: string): string | null {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

interface SingleCitationProps {
  source: DataSource;
  onNavigate?: () => void;
}

function SingleCitation({ source, onNavigate }: SingleCitationProps) {
  const navigate = useNavigate();
  const Icon = SOURCE_ICONS[source.type] || FileText;
  const label = SOURCE_LABELS[source.type] || 'Source';
  const colorClass = SOURCE_COLORS[source.type] || 'bg-slate-100 text-slate-700';
  const formattedDate = formatDate(source.date);

  const handleClick = () => {
    const citationEvent = sageEvents.citationClicked({
      source_type: source.type,
      has_route: !!source.routePath,
    });
    trackEvent(citationEvent.event, citationEvent.properties);
    if (source.routePath) {
      navigate(source.routePath);
      onNavigate?.();
    }
  };

  const isClickable = !!source.routePath;

  return (
    <div
      className={cn(
        'group flex items-start gap-3 p-3 rounded-lg border transition-all',
        isClickable && 'hover:bg-slate-50 cursor-pointer hover:border-slate-300',
        'bg-white'
      )}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {/* Icon */}
      <div className={cn('p-2 rounded-lg shrink-0', colorClass.replace('text-', 'bg-').replace('-700', '-100'))}>
        <Icon className={cn('h-4 w-4', colorClass.split(' ')[1])} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-slate-900 truncate">{source.title}</span>
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', colorClass)}>
            {label}
          </Badge>
          {source.relevance && source.relevance > 0 && (
            <span className="text-[10px] text-muted-foreground">{source.relevance}% match</span>
          )}
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {formattedDate && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formattedDate}
            </span>
          )}
          {source.category && (
            <span className="text-xs text-muted-foreground">
              {source.category}
            </span>
          )}
        </div>

        {/* Excerpt */}
        {source.excerpt && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{source.excerpt}</p>
        )}
      </div>

      {/* Navigate indicator */}
      {isClickable && (
        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      )}
    </div>
  );
}

export function CitationCard({ sources, className }: CitationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  // Group sources by type for better organization
  const groupedSources = sources.reduce((acc, source) => {
    const type = source.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(source);
    return acc;
  }, {} as Record<string, DataSource[]>);

  const uniqueTypes = Object.keys(groupedSources);
  const totalSources = sources.length;
  const showExpand = totalSources > 3;
  const displaySources = isExpanded ? sources : sources.slice(0, 3);

  return (
    <div className={cn('mt-4 pt-4 border-t border-slate-200', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            Sources ({totalSources})
          </span>
          {/* Type indicators */}
          <div className="flex gap-1">
            {uniqueTypes.slice(0, 4).map((type) => {
              const Icon = SOURCE_ICONS[type as DataSource['type']] || FileText;
              return (
                <div
                  key={type}
                  className="h-5 w-5 rounded flex items-center justify-center bg-slate-100"
                  title={`${groupedSources[type].length} ${SOURCE_LABELS[type as DataSource['type']] || type}(s)`}
                >
                  <Icon className="h-3 w-3 text-slate-500" />
                </div>
              );
            })}
            {uniqueTypes.length > 4 && (
              <span className="text-xs text-muted-foreground">+{uniqueTypes.length - 4}</span>
            )}
          </div>
        </div>

        {showExpand && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show all ({totalSources})
              </>
            )}
          </Button>
        )}
      </div>

      {/* Source list */}
      <div className="space-y-2">
        {displaySources.map((source, idx) => (
          <SingleCitation key={`${source.id}-${idx}`} source={source} />
        ))}
      </div>

      {/* Collapsed indicator */}
      {!isExpanded && totalSources > 3 && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {totalSources - 3} more source{totalSources - 3 !== 1 ? 's' : ''} hidden
        </p>
      )}
    </div>
  );
}

export default CitationCard;
